import React, { useCallback, useState, useRef, useEffect } from "react";
import "./YearSelectSlider.css";

interface YearSliderProps {
  value: number;
  years: number[];
  handleYearSliderChange: (event: Event, newValue: number | number[]) => void;
}

const YearSelectSlider: React.FC<YearSliderProps> = ({ 
  value, 
  handleYearSliderChange, 
  years
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLInputElement>(null);
  
  const currentIndex = years.indexOf(value);
  const safeIndex = currentIndex !== -1 ? currentIndex : 0;
  
  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);
  
  // Calculate percentage for positioning
  const percentage = years.length > 1 ? (safeIndex / (years.length - 1)) * 100 : 0;
  
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(e.target.value);
    if (index >= 0 && index < years.length) {
      const event = new Event('change', { bubbles: true });
      handleYearSliderChange(event, index);
    }
  }, [years, handleYearSliderChange]);
  
  const handleMouseDown = () => {
    setIsDragging(true);
    setShowTooltip(true);
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => setShowTooltip(false), 100);
  };
  
  return (
    <div className="year-section">
      <span className="filter-section-title">
        Year <span className="year-range">({minYear}-{maxYear})</span>
      </span>
      
      <div className="mui-style-slider-container">
        <input
          ref={sliderRef}
          type="range"
          className="mui-style-slider-input"
          min={0}
          max={years.length - 1}
          step={1}
          value={safeIndex}
          onChange={handleChange}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => !isDragging && setShowTooltip(false)}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleMouseUp}
        />
        
        <div className="mui-style-slider-track-container">
          {/* Rail (gray background) */}
          <div className="mui-style-slider-rail" />
          
          {/* Active track (turquoise) */}
          <div 
            className="mui-style-slider-track" 
            style={{ width: `${percentage}%` }}
          />
          
          {/* Marks */}
          {years.map((_, index) => {
            const markPercentage = years.length > 1 ? (index / (years.length - 1)) * 100 : 0;
            return (
              <div
                key={index}
                className={`mui-style-slider-mark ${index <= safeIndex ? 'active' : ''}`}
                style={{ left: `${markPercentage}%` }}
              />
            );
          })}
          
          {/* Thumb */}
          <div 
            className={`mui-style-slider-thumb ${isDragging ? 'dragging' : ''}`}
            style={{ left: `${percentage}%` }}
          >
            {(showTooltip || isDragging) && (
              <div className="mui-style-slider-tooltip">
                {years[safeIndex]}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default YearSelectSlider;